import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import PageHeader from "../PageHeader";
import { trainingApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";
import BandsSection from "./settings/BandsSection";
import BodyweightSection from "./settings/BodyweightSection";
import CapsSection from "./settings/CapsSection";
import EquipmentSection from "./settings/EquipmentSection";
import { gearList } from "./settings/gear";
import PrioritiesSection from "./settings/PrioritiesSection";
import SeedsSection from "./settings/SeedsSection";
import UnitsSection from "./settings/UnitsSection";
import VerticalPullSection from "./settings/VerticalPullSection";
import {
  buildPatch,
  fieldId,
  isPlaced,
  listWords,
  placePath,
  sameAt,
  savedMessage,
  SECTION_NAMES,
  sectionWords,
  shownBodyweight,
  toForm,
} from "./settings/settingsForm";

const INVALID = "invalid"; // focus request: the first marked field
// The sections, in page order, for the jump links (W20).
const JUMPS = [
  ["training-settings-units", "Units"],
  ["training-settings-equipment", "Equipment"],
  ["training-settings-bands", "Bands"],
  ["training-settings-bodyweight", "Bodyweight"],
  ["training-settings-vertical-pull", "Vertical pull"],
  ["training-settings-priorities", "Priorities"],
  ["training-settings-caps", "Weekly caps"],
  ["training-settings-seeds", "Seeds"],
];

/**
 * Settings (walkthrough 1, spec §3): units, time zone and session length,
 * equipment, bands, bodyweight, vertical pull, priorities, weekly caps and
 * seeds — one form, each section saying what it does to the plan.
 *
 * Save sends one PATCH carrying only the sections that changed
 * (settings/settingsForm.js `buildPatch`), then hands the server's answer
 * to the layout (`applySettings`), so Today plans with it at once. Errors
 * — the form's own checks and the server's nested ones, flattened to
 * dotted paths — land on the input they belong to; a server error stays
 * on its field until that field changes. Anything with no field of its
 * own goes in the banner above Save.
 */
function TrainingSettings() {
  const { settings, exercises, applySettings } = useOutletContext() || {};
  // The settings the form started from; the PATCH is the difference.
  const [base, setBase] = useState(settings);
  const [form, setForm] = useState(() => toForm(settings));
  const [attempted, setAttempted] = useState(false); // show the form's own checks
  // The last refusal: {errors (by dotted path), sent (the form as sent),
  // banner (the server's own words, if it gave any)}.
  const [server, setServer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [focusRequest, setFocusRequest] = useState(null);
  const mounted = useRef(true);
  const formRef = useRef(null);

  const draft = useMemo(() => buildPatch(base, form), [base, form]);
  const dirty = draft.sections.length > 0;

  // The layout's settings moved (another page saved, or our own save came
  // back through applySettings): take them — unless edits are in progress,
  // which keep the baseline they were made against.
  const [seen, setSeen] = useState(settings);
  if (settings !== seen) {
    setSeen(settings);
    if (!dirty && settings) {
      setBase(settings);
      setForm(toForm(settings));
    }
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Leaving with unsaved edits asks first (a reload or closing the tab).
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!focusRequest) return;
    const target =
      focusRequest.id === INVALID
        ? formRef.current?.querySelector('[aria-invalid="true"]')
        : document.getElementById(focusRequest.id);
    target?.focus();
  }, [focusRequest]);

  const errors = useMemo(() => {
    const out = {};
    if (server) {
      for (const [path, message] of Object.entries(server.errors)) {
        if (sameAt(server.sent, form, path)) out[path] = [].concat(message).join(" ");
      }
    }
    if (attempted) Object.assign(out, draft.errors);
    return out;
  }, [server, form, attempted, draft.errors]);
  const errorFor = useCallback((path) => errors[path] ?? null, [errors]);
  const unplaced = Object.entries(errors).filter(([path]) => !isPlaced(path, form));
  // The banner: the server's own words when it gave any; otherwise a
  // pointer to the marked fields while any are still marked.
  let banner = server?.banner ?? null;
  if (!banner && Object.keys(errors).length > 0) {
    banner = "Not saved yet — fix the marked fields, then save again.";
  }

  const change = (updater) => {
    setSaved("");
    setForm(updater);
  };
  const setField = (field, value) => change((f) => ({ ...f, [field]: value }));

  const reenterEquipment = (units) => {
    change((f) => ({
      ...f,
      equipment: {
        ...f.equipment,
        units,
        dumbbell_max: "",
        dumbbell_step: "",
        barbell_step: "",
        cable_step: "",
      },
    }));
    setFocusRequest({ id: fieldId("equipment.dumbbell_max"), n: Date.now() });
  };

  const undo = () => {
    setForm(toForm(base));
    setAttempted(false);
    setServer(null);
    setSaved("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const { patch, sections, errors: problems } = draft;
    setSaved("");
    setAttempted(true);
    if (Object.keys(problems).length > 0) {
      setFocusRequest({ id: INVALID, n: Date.now() });
      return;
    }
    if (sections.length === 0) {
      setServer(null);
      setSaved("Nothing to save — you haven't changed anything.");
      return;
    }
    const sent = form;
    setSaving(true);
    setServer(null);
    try {
      const { data } = await trainingApi.settings.patch(patch);
      // The layout outlives this page: it takes the confirmed settings even
      // when the person left before the answer (a stale copy there seeds
      // the next visit's form, and a later whole-list save would revert
      // this one). Only the page's own state waits on it being here.
      applySettings?.(data);
      if (!mounted.current) return;
      setBase(data);
      setForm(toForm(data));
      setAttempted(false);
      setSaved(savedMessage(sections, patch, data));
    } catch (err) {
      if (!mounted.current) return;
      const { formError, fieldErrors } = parseApiError(err, "Couldn't reach mono.");
      const placed = Object.fromEntries(
        Object.entries(fieldErrors).map(([path, message]) => [placePath(path), message]),
      );
      setServer({
        errors: placed,
        sent,
        banner: formError
          ? `Not saved: ${/[.!?]$/.test(formError) ? formError : `${formError}.`} Your changes are still here — save again when ready.`
          : null,
      });
      setFocusRequest({ id: INVALID, n: Date.now() });
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const pending = sectionWords(draft.sections);
  let status;
  if (dirty) status = `Not saved yet: ${listWords(pending)}.`;
  else if (base?.saved === false) status = "Starting settings — plans use them as they are.";
  else status = "Everything is saved.";
  const startingGear = gearList(base?.equipment);

  return (
    <div className="page-container mt-6 pt-4 pb-6">
      <div className="mx-auto max-w-3xl">
        <PageHeader
          title="Settings"
          subtitle="Your gym, your bands and bodyweight, and what to prioritize. Change anything — the next plan follows."
        />
        {/* The starting settings as they are — the gear read from them,
            addressed to whoever is reading (W20). */}
        {base?.saved === false && (
          <p className="mb-4 rounded-lg border bg-card p-4 text-sm">
            These are the starting settings —{" "}
            {startingGear.length > 0 ? `equipment: ${startingGear.join(", ")}.` : "no equipment yet."} They
            aren&apos;t saved yet, and plans already use them as they are: change anything to match
            your gym, then save.
          </p>
        )}
        {/* A long page (W20; ~7,800 px at 390): each section a tap away.
            Buttons, not #hash links — the page's URL stays as it is. */}
        <nav aria-label="Jump to a section" className="mb-4 flex flex-wrap gap-x-1 text-sm">
          {JUMPS.map(([target, label]) => (
            <Button
              key={target}
              type="button"
              variant="link"
              className="h-11 px-2 md:h-8"
              onClick={() => {
                const section = document.getElementById(`${target}-section`);
                section?.scrollIntoView?.({ block: "start" });
                section?.querySelector("h2")?.focus({ preventScroll: true });
              }}
            >
              {label}
            </Button>
          ))}
        </nav>
        <form ref={formRef} onSubmit={save} noValidate aria-label="Training settings">
          <fieldset disabled={saving} className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
            <UnitsSection
              form={form}
              settings={base}
              onChange={setField}
              onReenterEquipment={reenterEquipment}
              errorFor={errorFor}
            />
            <EquipmentSection
              equipment={form.equipment}
              settings={base}
              exercises={exercises}
              onChange={(equipment) => setField("equipment", equipment)}
              errorFor={errorFor}
            />
            <BandsSection
              bands={form.bands}
              units={form.equipment.units}
              onChange={(bands) => setField("bands", bands)}
              errorFor={errorFor}
            />
            <BodyweightSection
              value={shownBodyweight(base, form)}
              onChange={(value) =>
                change((f) => ({ ...f, bodyweight: value, bodyweightTouched: true }))
              }
              units={form.units}
              settings={base}
              error={errorFor("bodyweight") || undefined}
            />
            <VerticalPullSection
              value={form.vertical_pull}
              onChange={(value) => setField("vertical_pull", value)}
              error={errorFor("vertical_pull") || undefined}
            />
            <PrioritiesSection
              priorities={form.priorities}
              onChange={(priorities) => setField("priorities", priorities)}
              errorFor={errorFor}
            />
            <CapsSection
              caps={form.target_caps}
              onChange={(caps) => setField("target_caps", caps)}
              errorFor={errorFor}
            />
            <SeedsSection
              seeds={form.seeds}
              settings={base}
              exercises={exercises}
              units={form.units}
              onChange={(seeds) => setField("seeds", seeds)}
              errorFor={errorFor}
            />
          </fieldset>

          {/* Save stays in reach on a long page: it sticks to the bottom of
              the screen while the form scrolls under it. data-bottom-bar:
              the page keeps that much room below a focused field
              (tailwind.css), so tabbing never lands under the bar. */}
          <div
            data-bottom-bar=""
            className="sticky bottom-0 z-30 mt-4 flex flex-col gap-2 rounded-lg border bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-md"
          >
            {banner && (
              <Alert variant="destructive">
                <AlertDescription>
                  <p className="m-0">{banner}</p>
                  {unplaced.length > 0 && (
                    <ul className="m-0 list-disc pl-5">
                      {unplaced.map(([path, message]) => (
                        <li key={path}>
                          {SECTION_NAMES[path.split(".")[0]] ?? path}: {message}
                        </li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 min-w-0 flex-1 basis-48 text-sm">
                <span role="status">{saved}</span>
                {!saved && status}
              </p>
              {/* The buttons wrap as one group, Save last (rightmost). */}
              <div className="ml-auto flex shrink-0 gap-2">
                {dirty && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 md:h-9"
                    onClick={undo}
                    disabled={saving}
                  >
                    Undo changes
                  </Button>
                )}
                <Button type="submit" className="h-11 md:h-9" disabled={saving}>
                  {saving && <Loader2 className="animate-spin" aria-hidden="true" />}
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrainingSettings;
